import { WALKTHROUGH_ABANDONMENT_TIMEOUT_MS, WALKTHROUGH_STATE_KEY_PREFIX } from './constants';
import type { WalkthroughState } from './types';

/**
 * Estado persistente del walkthrough activo. Solo permite UNO por
 * workspace (namespace de apiKey). Vive en `localStorage` para sobrevivir
 * recargas de página: el step actual + el guideId alcanzan para resumir
 * desde donde quedó el usuario.
 *
 * Si pasan más de `WALKTHROUGH_ABANDONMENT_TIMEOUT_MS` sin progreso, se
 * considera abandonado: la próxima llamada a `current()` lo limpia
 * silenciosamente. NO se reporta `dismissed` al backend en ese caso —
 * abandonarse no es un acto del usuario.
 */
export class WalkthroughStateManager {
  private readonly storageKey: string;
  private state: WalkthroughState | null = null;

  constructor(namespace: string) {
    this.storageKey = `${WALKTHROUGH_STATE_KEY_PREFIX}${namespace}`;
    this.state = this.load();
    if (this.state && this.isAbandoned(this.state)) {
      this.clear();
    }
  }

  /**
   * Estado del walkthrough activo. Devuelve `null` si no hay ninguno o si
   * el último progreso fue hace más del timeout (en ese caso también
   * limpia el storage como side-effect).
   */
  current(): WalkthroughState | null {
    if (!this.state) return null;
    if (this.isAbandoned(this.state)) {
      this.clear();
      return null;
    }
    return this.state;
  }

  /** Atajo legible para el controller. */
  hasActive(): boolean {
    return this.current() !== null;
  }

  /**
   * Inicia un nuevo walkthrough en step 0. Sobreescribe cualquier estado
   * previo — los callers (controller) son responsables de verificar
   * `hasActive()` antes si necesitan exclusividad.
   */
  start(guideId: string, totalSteps: number): WalkthroughState {
    const now = Date.now();
    this.state = {
      guideId,
      totalSteps,
      currentStepIndex: 0,
      startedAt: now,
      lastProgressAt: now,
    };
    this.persist();
    return this.state;
  }

  /**
   * Avanza al siguiente step. Si el step resultante excede `totalSteps`,
   * devuelve `null` y NO toca el estado — el controller debe interpretar
   * eso como "ya estamos en el último, no hay próximo" y disparar
   * `complete` vía el handler del botón "Finalizar".
   */
  advance(): WalkthroughState | null {
    if (!this.state) return null;

    const next = this.state.currentStepIndex + 1;
    if (next >= this.state.totalSteps) return null;

    this.state = {
      ...this.state,
      currentStepIndex: next,
      lastProgressAt: Date.now(),
    };
    this.persist();
    return this.state;
  }

  /**
   * Retrocede al step anterior. En step 0 no hace nada y devuelve el
   * estado tal cual (el botón "Atrás" no debería estar visible en el
   * primer step, pero por defensa el manager también lo guard-ea).
   */
  back(): WalkthroughState | null {
    if (!this.state) return null;
    if (this.state.currentStepIndex === 0) return this.state;

    this.state = {
      ...this.state,
      currentStepIndex: this.state.currentStepIndex - 1,
      lastProgressAt: Date.now(),
    };
    this.persist();
    return this.state;
  }

  /** Limpia el estado activo (skip, complete, abandono, guía borrada). */
  clear(): void {
    this.state = null;
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      // Storage bloqueado — no rompemos la app.
    }
  }

  private isAbandoned(state: WalkthroughState): boolean {
    return Date.now() - state.lastProgressAt > WALKTHROUGH_ABANDONMENT_TIMEOUT_MS;
  }

  private load(): WalkthroughState | null {
    if (typeof localStorage === 'undefined') return null;
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as unknown;
      if (!parsed || typeof parsed !== 'object') return null;
      const candidate = parsed as Partial<WalkthroughState>;
      if (
        typeof candidate.guideId !== 'string' ||
        typeof candidate.totalSteps !== 'number' ||
        typeof candidate.currentStepIndex !== 'number' ||
        typeof candidate.startedAt !== 'number' ||
        typeof candidate.lastProgressAt !== 'number'
      ) {
        return null;
      }
      return candidate as WalkthroughState;
    } catch {
      return null;
    }
  }

  private persist(): void {
    if (typeof localStorage === 'undefined' || !this.state) return;
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.state));
    } catch {
      // Quota exceeded — silently fail. El walkthrough en memoria sigue.
    }
  }
}
